import { faGlobe, faUser, faUserPlus, faUsers } from '@fortawesome/pro-regular-svg-icons';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { InfiniteLoadTrigger } from 'src/components/InfiniteLoadTrigger';
import { Link } from 'src/components/Link';
import { Meta } from 'src/components/Meta';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Theme, useMediaQuery } from 'src/components/muiTheme/utils';
import { useOptionalSession, useSession } from 'src/contexts/SessionContext';
import { fetchDashboardActivities } from 'src/models/activity/activity';
import { GroupMemberSearchResult } from 'src/models/group/_types';
import { fetchUserById } from 'src/models/user/user';
import { getUserDisplayName, isProTrainer } from 'src/models/user/utils';
import { ActivityItem } from 'src/shared/ActivityItem/ActivityItem';
import { AdaptiveSideMenu } from 'src/shared/AdaptiveSideMenu/AdaptiveSideMenu';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { GroupMemberAutocomplete } from 'src/shared/GroupMemberAutocomplete';
import { Identifier } from 'src/types/utilityTypes';

type ActivityTabType = 'clients' | 'user' | 'following' | 'all' | 'group';

const ActivityPage = () => {
  const { session, sessionLoading, user, userLoading } = useOptionalSession();

  if (sessionLoading || userLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!session?.id || !user?.id) {
    return <Navigate to="/login" replace />;
  }

  return <ActivityPageContent />;
};

const ActivityPageContent = () => {
  const { session, user, sessionTrainer } = useSession();
  const { t } = useTranslation('translation', { keyPrefix: 'activity' });

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [searchParams, setSearchParams] = useSearchParams();

  const isUserProTrainer = isProTrainer(user);

  const showSitewide =
    (!session.privateUsers && session?.showActivity) || isUserProTrainer;

  const activityType =
    (searchParams.get('activity_type') as ActivityTabType) || 'user';
  const groupId = searchParams.get('group_id') || undefined;
  const userId = searchParams.get('user_id') || undefined;
  const inIframe = searchParams.get('iframe') === 'true';
  const allowFiltering = user.nickname && activityType === 'group';

  const [currentTab, setCurrentTab] = useState(activityType);
  const [currentMember, setCurrentMember] =
    useState<GroupMemberSearchResult | null>(null);

  const { data: userData, isLoading: isUserLoading } = useQuery({
    queryKey: [userId],
    queryFn: () => fetchUserById(userId!),
    enabled: !!userId && !currentMember,
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ['activities', currentTab, user?.id, groupId, userId],
    queryFn: async ({ pageParam }) => {
      const lastItemId: Identifier | undefined =
        typeof pageParam === 'number' && pageParam === 1
          ? undefined
          : (pageParam as Identifier);
      const page = typeof pageParam === 'number' ? pageParam : 1;

      const response = await fetchDashboardActivities({
        activityType: currentTab,
        groupId,
        userId,
        page: lastItemId ? undefined : page,
        perPage: 10,
        lastItemId,
      });
      return response;
    },
    enabled: !!user?.id,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 10 && lastPage.length === 0) return undefined;

      const lastItem = lastPage[lastPage.length - 1];
      if (!lastItem?.id) return undefined;

      return lastItem.id as Identifier;
    },
    initialPageParam: 1 as Identifier | number,
  });

  const updateSearchParams = (key, val) => {
    setSearchParams(
      (pre) => {
        pre.set(key, val);
        return pre;
      },
      { replace: true }
    );
  };

  const handleTabChange = (newValue) => {
    updateSearchParams('activity_type', newValue);
    setCurrentTab(newValue);
  };

  const activities = data?.pages.flatMap((page) => page) || [];

  const tabs = [
    {
      value: 'clients',
      label: t('tabs.clients'),
      icon: faUsers,
      show: isUserProTrainer,
    },
    {
      value: 'user',
      label: t('tabs.me'),
      icon: faUser,
      show: !isUserProTrainer,
    },
    {
      value: 'following',
      label: t('tabs.following'),
      icon: faUserPlus,
      show: !sessionTrainer,
    },
    {
      value: 'all',
      label: t('tabs.sitewide'),
      icon: faGlobe,
      show: showSitewide,
    },
  ].filter((tab) => tab.show);

  const pageTitle = t('title');

  return (
    <>
      <Meta title={pageTitle} brand={session?.brand} />
      <AppFrameConfig
        id="activity-id"
        containerMaxWidth={'xl'}
        title={pageTitle}
        disabled={inIframe}
      />
      {allowFiltering && (
        <SectionToolbar>
          {userId ? (
            isUserLoading ? null : (
              <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                <Typography>
                  {t('viewing', {
                    name: currentMember?.name || getUserDisplayName(userData),
                  })}
                </Typography>
                <Link
                  component="button"
                  onClick={() => {
                    updateSearchParams('user_id', '');
                  }}
                >
                  ({'clear'})
                </Link>
              </Stack>
            )
          ) : (
            <GroupMemberAutocomplete
              types={[`group_members_${groupId}`]}
              value={userId}
              onChange={(res: GroupMemberSearchResult) => {
                updateSearchParams('user_id', res?.id || '');
                setCurrentMember(res);
              }}
              sx={{ width: '100%' }}
              clearOptions
            />
          )}
        </SectionToolbar>
      )}
      <Stack direction={mdDown ? 'column' : 'row'}>
        {!inIframe && (
          <AdaptiveSideMenu
            navItems={tabs}
            activeValue={activityType}
            onValueChange={(value) => handleTabChange(value)}
            collapsible={true}
            title={pageTitle}
            id="activity-id"
          />
        )}
        <Box sx={{ width: '100%', transition: 'all 0.3s ease' }}>
          <Stack spacing={3} pb={3}>
            {isLoading && (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            )}

            {error && (
              <Box p={4}>
                <Typography variant="body1" color="error" align="center">
                  {t('error')}
                </Typography>
              </Box>
            )}

            {!isLoading && !error && (
              <>
                {activities.length === 0 ? (
                  <Box p={4}>
                    <Typography variant="h5" align="center">
                      {t('empty')}
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={3}>
                    <>
                      {activities.map((activity) => (
                        <ActivityItem
                          key={activity.id}
                          activity={activity}
                          dateFormatter="long"
                          onlyCreatedAt={true}
                          showNote={false}
                        />
                      ))}
                      <InfiniteLoadTrigger
                        fetching={isFetchingNextPage}
                        onFetch={() => hasNextPage && fetchNextPage()}
                      >
                        {isFetchingNextPage && !isLoading && (
                          <CircularProgress size="small" />
                        )}
                      </InfiniteLoadTrigger>
                    </>
                  </Stack>
                )}
              </>
            )}
          </Stack>
        </Box>
      </Stack>
    </>
  );
};

export default ActivityPage;
